import { useEffect, useState } from "react";
import { ChevronDown, Sparkles, ShieldCheck, HelpCircle, Info } from "lucide-react";
import { useData } from "@/context/DataContext";
import api from "@/lib/api";
import { Card } from "@/components/common/PageHeader";
import { StatusChip } from "@/components/common/StatusChip";
import { ScoreGauge } from "@/components/common/ScoreGauge";
import { Loading, DeltaPill } from "@/pages/_shared";
import { ScoreSimulator } from "@/components/common/ScoreSimulator";
import { ActionPlan } from "@/components/common/ActionPlan";

const grade = (v) => (v >= 85 ? "A" : v >= 70 ? "B" : v >= 55 ? "C" : v >= 40 ? "D" : "F");
const gColor = (v) => (v >= 85 ? "#059669" : v >= 70 ? "#0ea5e9" : v >= 55 ? "#1a5fd0" : v >= 40 ? "#d97706" : "#dc2626");

function FactorRow({ f }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-100 last:border-0" data-testid="readiness-factor">
      <button onClick={() => setOpen((v) => !v)} className="flex w-full items-center gap-3 py-3 text-left">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[13px] font-bold text-white" style={{ background: gColor(f.value) }}>{grade(f.value)}</div>
        <span className="flex-1 text-[14px] font-medium text-slate-800">{f.name}</span>
        <DeltaPill value={f.change} />
        <span className="w-10 text-right font-mono text-[13px] text-slate-600">{f.value}</span>
        <ChevronDown size={16} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="grid gap-3 pb-4 pl-11 text-[12px] sm:grid-cols-3">
          <div><div className="font-mono text-[9px] uppercase tracking-wider text-[#1a5fd0]">Why it matters</div><p className="mt-0.5 text-slate-600">{f.why}</p></div>
          <div><div className="font-mono text-[9px] uppercase tracking-wider text-[#1a5fd0]">Data used</div><p className="mt-0.5 text-slate-600">{f.data_used}</p></div>
          <div><div className="font-mono text-[9px] uppercase tracking-wider text-[#1a5fd0]">How to improve</div><p className="mt-0.5 text-slate-600">{f.improve}</p></div>
        </div>
      )}
    </div>
  );
}

export default function FundingReadiness() {
  const { snapshot } = useData();
  const [tab, setTab] = useState("Loans");
  const [fr, setFr] = useState(null);
  useEffect(() => { api.get("/funding-readiness").then(({ data }) => setFr(data)).catch(() => setFr(null)); }, []);
  if (!snapshot || !fr) return <Loading />;

  if (!snapshot.is_demo || !fr.factors || fr.factors.length === 0) {
    return (
      <div className="mx-auto max-w-4xl animate-fade-up" data-testid="funding-readiness-page">
        <h1 className="font-head text-3xl font-extrabold tracking-tight text-slate-900">Funding readiness</h1>
        <Card className="mt-6 flex flex-col items-center justify-center gap-2 py-20 text-center" data-testid="readiness-empty">
          <div className="font-head text-lg font-bold text-slate-800">Complete your profile to calculate your MMT Funding Readiness</div>
          <p className="max-w-md text-[13px] text-slate-500">Connect your business identity, credit and bank data and we'll compute your readiness — no estimated or sample scores are shown.</p>
        </Card>
      </div>
    );
  }
  const providers = snapshot.business_credit.providers;
  const offer = snapshot.capital_marketplace.offers.find((o) => o.status === "PREQUALIFIED") || snapshot.capital_marketplace.offers[0];
  const high = fr.factors.filter((f) => f.weight >= 15);
  const low = fr.factors.filter((f) => f.weight < 15);
  const strengths = [...fr.factors].filter((f) => f.value >= 60).sort((a, b) => b.value - a.value).slice(0, 3);
  const gaps = [...fr.factors].filter((f) => f.value < 60).sort((a, b) => a.value - b.value);
  const missing = providers.filter((p) => p.score == null);
  const frLevers = fr.factors.filter((f) => f.value < 100).map((f) => ({ id: f.name, label: f.improve, points: Math.max(2, Math.round((100 - f.value) * f.weight * 0.0425)), detail: f.name }));
  const frPlan = [...frLevers].sort((a, b) => b.points - a.points).slice(0, 3).map((l) => ({ title: l.label, points: l.points, detail: l.detail }));

  return (
    <div className="mx-auto max-w-4xl animate-fade-up" data-testid="funding-readiness-page">
      <h1 className="font-head text-3xl font-extrabold tracking-tight text-slate-900">Funding readiness</h1>
      <p className="mt-1 text-[14px] text-slate-500">See how lenders evaluate your business and the steps you can take to improve your approval odds.</p>
      <div className="mt-4 flex gap-5 border-b border-slate-200">
        {["Loans", "Credit cards", "Trade credit"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`readiness-tab-${t.toLowerCase().replace(/\s/g, "-")}`}
            className={`pb-2.5 text-[14px] font-semibold transition-colors ${tab === t ? "border-b-2 border-[#1a5fd0] text-slate-900" : "text-slate-400"}`}>{t}</button>
        ))}
      </div>

      {/* Virtual lender analysis */}
      <div className="mt-5 grid gap-4 md:grid-cols-3">
        <Card className="p-5 md:col-span-2">
          <div className="mb-2 flex items-center justify-between">
            <span className="font-head text-[14px] font-bold text-slate-900">Virtual lender analysis</span>
            <span className="flex items-center gap-1 rounded-md bg-[#1a5fd0]/10 px-2 py-0.5 font-mono text-[10px] font-bold uppercase text-[#1a5fd0]"><Sparkles size={11} /> AI</span>
          </div>
          <div className="text-[12px] font-semibold text-slate-500">Strengths</div>
          <ul className="mt-1 space-y-1.5 text-[13px] text-slate-700">
            {strengths.length ? strengths.map((f) => (
              <li key={f.name}>• <b>{f.name}</b> ({f.value}/100) — {f.data_used}</li>
            )) : <li className="text-slate-400">Complete your Business Profile to surface your funding strengths.</li>}
          </ul>
          {gaps.length > 0 && (
            <>
              <div className="mt-3 text-[12px] font-semibold text-slate-500">Where to focus</div>
              <ul className="mt-1 space-y-1.5 text-[13px] text-slate-700">
                {gaps.slice(0, 3).map((f) => <li key={f.name}>• <b>{f.name}</b> ({f.value}/100) — {f.improve}</li>)}
              </ul>
            </>
          )}
        </Card>
        <Card className="flex flex-col items-center justify-center p-5 text-center">
          <ScoreGauge score={fr.score} scale={fr.scale} tier={fr.tier} size={120} testId="funding-readiness-score-gauge" />
          <div className="mt-2 text-[12px] text-slate-500">You're missing <b>{missing.length}</b> data source{missing.length !== 1 ? "s" : ""}</div>
          {missing.slice(0, 1).map((m) => <StatusChip key={m.provider} state={m.state} className="mt-1" />)}
        </Card>
      </div>

      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        <ScoreSimulator baseScore={fr.score} scale={fr.scale} levers={frLevers} />
        <ActionPlan actions={frPlan} />
      </div>

      {/* Business credit scores */}
      <div className="mt-6">
        <h2 className="font-head text-lg font-extrabold text-slate-900">Business credit scores</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {providers.map((p) => (
            <Card key={p.provider} className="flex items-center gap-3 p-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-full text-[12px] font-bold text-white" style={{ background: p.score ? gColor((p.score / p.scale) * 100) : "#cbd5e1" }}>{p.score ? grade((p.score / p.scale) * 100) : "?"}</div>
              <div className="flex-1"><div className="text-[13px] font-semibold text-slate-800">{p.score_label || p.provider}</div><div className="text-[11px] text-slate-400">{p.provider}</div></div>
              {p.score != null ? <span className="font-head text-lg font-extrabold text-slate-900">{p.score}</span> : <StatusChip state={p.state} />}
            </Card>
          ))}
        </div>
      </div>

      {/* Disclaimer */}
      <div className="mt-5 flex items-start gap-2 rounded-lg bg-amber-50 px-4 py-3 text-[12px] text-amber-800">
        <Info size={15} className="mt-0.5 shrink-0" />{fr.disclaimer}
      </div>

      {/* High impact */}
      <div className="mt-6">
        <h2 className="font-head text-lg font-extrabold text-slate-900">High impact</h2>
        <p className="text-[13px] text-slate-500">These factors have a significant impact on how lenders see your level of risk.</p>
        <Card className="mt-3 px-4">{high.map((f) => <FactorRow key={f.name} f={f} />)}</Card>
      </div>

      {/* Deal breakers */}
      <div className="mt-6">
        <h2 className="font-head text-lg font-extrabold text-slate-900">Deal breakers</h2>
        <p className="text-[13px] text-slate-500">These are critical factors for lenders and are typically out of your control.</p>
        <Card className="mt-3 divide-y divide-slate-100 px-4">
          {[["Time in business", `${Math.floor(fr.company.business_age_months / 12)} yrs ${fr.company.business_age_months % 12} mo`, fr.company.business_age_months >= 24 ? "A" : fr.company.business_age_months >= 12 ? "B" : "D"], ["Industry", fr.company.industry, "B"], ["Bankruptcy history", "0", "A"]].map(([n, v, g]) => (
            <div key={n} className="flex items-center gap-3 py-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-[12px] font-bold text-white">{g}</div>
              <span className="flex-1 text-[14px] font-medium text-slate-800">{n}</span>
              <span className="text-[13px] text-slate-600">{v}</span>
            </div>
          ))}
        </Card>
      </div>

      {/* Moderate & low */}
      <div className="mt-6">
        <h2 className="font-head text-lg font-extrabold text-slate-900">Moderate and low impact</h2>
        <p className="text-[13px] text-slate-500">Improving and maintaining these factors will meaningfully reduce your risk.</p>
        <Card className="mt-3 px-4">{low.map((f) => <FactorRow key={f.name} f={f} />)}</Card>
      </div>

      {/* Assessment / loan option */}
      <div className="mt-6">
        <h2 className="font-head text-lg font-extrabold text-slate-900">Based on your assessment, you have funding options</h2>
        <Card className="mt-3 p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div><div className="font-head text-[15px] font-bold text-slate-900">{offer.name}</div><div className="text-[12px] text-slate-400">{offer.provider} · {offer.amount} · {offer.terms}</div></div>
            <span className="rounded-md bg-emerald-50 px-2 py-1 font-mono text-[10px] font-bold uppercase text-emerald-600">{offer.status}</span>
          </div>
          <div className="mt-2 flex items-center gap-2"><StatusChip state={offer.state} /></div>
        </Card>
      </div>

      {/* Coaching CTA */}
      <Card className="mt-4 mb-6 flex items-center justify-between gap-4 bg-[#0f172a] p-5 text-white">
        <div className="flex items-center gap-3">
          <ShieldCheck size={22} className="text-[#5a8bff]" />
          <div><div className="font-head text-[15px] font-bold">Still have questions about your funding readiness?</div><div className="text-[12px] text-slate-400">Ask MMT AI to explain your assessment and next steps.</div></div>
        </div>
        <HelpCircle size={20} className="text-slate-500" />
      </Card>
    </div>
  );
}
